import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { Form, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { TextFieldElement } from "react-hook-form-mui";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiClientHooks } from "../bootstrapping/InitApiClient";
import { Helmet } from "react-helmet-async";
import { LocalFormSchema, zLocalFormSchema } from "../logic/applicationUpdateSchema";
import StringFormattedControlledDatePicker from "../components/StringFormattedControlledDatePicker";
import EnumBackedAutocomplete from "../components/EnumBackedAutocomplete";
import { asOptions, countryCodeMapping } from "../utils/option-data";
import OptionalTextField from "../components/OptionalTextField";
import { TestingInfoBlock, TestingInfoBlockFormErrors } from "../components/TestingInfo";
import { ensureUppercase, trimTextFields } from "../utils/forms";
import { applicationRefinement } from "../logic/dbsRules";
import { useRedirectToGDPRConsentIfRequired } from "./ApplicationGDPRConsent";
import FormButtons from "../components/FormButtons";
import TestingData from "../components/TestingData";
import { useActionPath } from "./ApplicationUpdate";
import TestApplicationAlert from "../components/TestApplicationAlert";

export const Element: React.FC = () => {
  const { application_id } = useParams<{ application_id?: string }>()
  if (!application_id) throw new Error('application_id is required')

  const getEBulkApplicationByID = apiClientHooks.useGetEBulkApplicationByID({ params: { id: application_id } })
  const readonly = getEBulkApplicationByID.data?.status !== 'draft'

  useRedirectToGDPRConsentIfRequired(application_id)
  const actionPath = useActionPath('addresses')

  // Build the Form
  const form = useForm<LocalFormSchema>({
    resolver: zodResolver(
      zLocalFormSchema
        .superRefine(applicationRefinement('birth'))
    ),
    mode: 'onBlur',
    defaultValues: {
      ...getEBulkApplicationByID.data?.data as any, // NOTE: This is a hack, as I don't understand the typescript error!
      DateOfBirth: getEBulkApplicationByID.data?.data.DateOfBirth ?? null, // Ensure we're always a controlled component
      AdditionalApplicantDetails: {
        ...getEBulkApplicationByID.data?.data.AdditionalApplicantDetails ?? {},
        BirthTown: getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthTown ?? '',
        BirthCountry: getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthCountry ?? '',
      },
    },
  })

  // Build the output
  const textFieldElementCommonProps = {
    control: form.control,
    margin: "normal" as const,
    fullWidth: true,
  }

  return <>
    <Helmet>
      <title>{readonly ? 'View' : 'Update'} Birth Details</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5" marginBottom={3}>
          {readonly ? 'View' : 'Update'} DBS Application
        </Typography>
        <TestApplicationAlert dbsApp={getEBulkApplicationByID.data} sx={{ marginBottom: 3 }} />
        <Typography component="h2" variant="h5" marginBottom={3}>
          Birth Details
        </Typography>
        <Typography variant="body1" marginBottom={1}>
          We need to know about your birth.
        </Typography>

        <Form action={actionPath.url} method="post" onBlur={() => {
          trimTextFields(form)
          ensureUppercase(form, 'AdditionalApplicantDetails.BirthTown')
          ensureUppercase(form, 'AdditionalApplicantDetails.BirthCounty')
          ensureUppercase(form, 'AdditionalApplicantDetails.BirthNationality')
        }}>
          {!readonly && (
            <TestingInfoBlock>
              <TestingData form={form} page="birth" />
            </TestingInfoBlock>
          )}
          <StringFormattedControlledDatePicker
            control={form.control}
            sx={{ marginTop: 3 }}
            label="Date of Birth"
            name="DateOfBirth"
            views={['day', 'month', 'year']}
            format="yyyy-MM-dd"
            disabled={readonly}
          />
          <TextFieldElement {...textFieldElementCommonProps} label="Birth Town" name="AdditionalApplicantDetails.BirthTown" disabled={readonly} />
          <OptionalTextField {...textFieldElementCommonProps} label="Birth County (optional)" name="AdditionalApplicantDetails.BirthCounty" disabled={readonly} />
          <EnumBackedAutocomplete
            control={form.control}
            sx={{ marginTop: 1 }}
            label="Birth Country"
            name={`AdditionalApplicantDetails.BirthCountry`}
            options={asOptions(countryCodeMapping)}
            disabled={readonly}
          />
          <OptionalTextField {...textFieldElementCommonProps} label="Birth Nationality (optional)" name="AdditionalApplicantDetails.BirthNationality" disabled={readonly} />

          <TestingInfoBlockFormErrors form={form} />

          <FormButtons actionPath={actionPath} form={form} readonly={readonly} />
        </Form>
      </Box>
    </Container>
  </>
}

export default Element
